import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Moon, Sun } from 'lucide-react';
import { db, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import ResellerAgreement from './ResellerAgreement';
import NotificationDropdown from './NotificationDropdown';

const Swal = typeof window !== "undefined" && window.Swal ? window.Swal : { fire: (msg) => window.alert(msg) };

export default function ResellerLayout() {
  const { currentUser, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementViewOnly, setAgreementViewOnly] = useState(false);
  const [resellerData, setResellerData] = useState(null);

  // Check for reseller agreement on mount
  useEffect(() => {
    async function checkResellerAgreement() {
      if (!currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setResellerData(userData);

          // Show agreement if not yet accepted
          if (!userData.reseller?.agreementAcceptedAt) {
            setShowAgreementModal(true);
            setAgreementViewOnly(false);
          }
        }
      } catch (error) {
        console.error('Error checking reseller agreement:', error);
      }
    }
    checkResellerAgreement();
  }, [currentUser]);

  const handleAcceptAgreement = async () => {
    try {
      const acceptResellerAgreement = httpsCallable(functions, 'acceptResellerAgreement');
      await acceptResellerAgreement();

      // Update local state
      setResellerData(prev => ({
        ...prev,
        reseller: {
          ...prev.reseller,
          agreementAcceptedAt: new Date(),
        }
      }));

      setShowAgreementModal(false);

      Swal.fire({
        title: 'Agreement Accepted!',
        text: 'Welcome to the Merch Masters Reseller Program',
        icon: 'success',
        confirmButtonColor: '#3b82f6',
      });
    } catch (error) {
      console.error('Error accepting agreement:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to accept agreement. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  const handleDeclineAgreement = () => {
    Swal.fire({
      title: 'Agreement Required',
      text: 'You must accept the reseller agreement to use this platform. You will now be signed out.',
      icon: 'warning',
      confirmButtonColor: '#3b82f6',
    }).then(() => {
      signOut();
      navigate('/login');
    });
  };

  const handleViewAgreement = () => {
    setAgreementViewOnly(true);
    setShowAgreementModal(true);
  };

  async function handleSignOut() {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Top Navigation */}
      <nav className="bg-blue-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-8">
              <h1 className="text-xl font-bold">Merch Masters - Reseller Portal</h1>
              <div className="flex gap-4">
                <NavLink
                  to="/reseller/dashboard"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-blue-700' : 'hover:bg-blue-500'
                    }`
                  }
                >
                  Dashboard
                </NavLink>
                <NavLink
                  to="/reseller/orders"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-blue-700' : 'hover:bg-blue-500'
                    }`
                  }
                >
                  Orders
                </NavLink>
                <NavLink
                  to="/reseller/custom-orders"
                  className={({ isActive }) =>
                    `px-3 text-purple-200 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-blue-700 text-purple-100' : 'hover:bg-blue-500 hover:text-white'
                    }`
                  }
                >
                  Custom Orders
                </NavLink>

                <NavLink
                  to="/reseller/payments"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-blue-700' : 'hover:bg-blue-500'
                    }`
                  }
                >
                  Payments
                </NavLink>
                <NavLink
                  to="/reseller/account"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-blue-700' : 'hover:bg-blue-500'
                    }`
                  }
                >
                  Account
                </NavLink>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm">{currentUser?.displayName || currentUser?.email}</span>
              <button
                onClick={toggleTheme}
                className="p-2 text-white hover:bg-blue-500 rounded-full transition-colors"
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <NotificationDropdown currentUser={currentUser} />
              {resellerData?.reseller?.agreementAcceptedAt && (
                <button
                  onClick={handleViewAgreement}
                  className="px-3 py-2 text-sm hover:bg-blue-500 rounded-md transition-colors"
                  title="View Agreement"
                >
                  📄 Agreement
                </button>
              )}
              <button
                onClick={handleSignOut}
                className="px-4 py-2 bg-blue-700 hover:bg-blue-800 rounded-md text-sm font-medium transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <Outlet />

      {/* Reseller Agreement Modal */}
      {showAgreementModal && resellerData && (
        <ResellerAgreement
          onAccept={handleAcceptAgreement}
          onClose={agreementViewOnly ? () => setShowAgreementModal(false) : handleDeclineAgreement}
          resellerPrice={resellerData.reseller?.resellerPrice}
          weeklyPaymentDate={resellerData.reseller?.weeklyPaymentDate}
          companyName={resellerData.reseller?.companyName}
          viewOnly={agreementViewOnly}
        />
      )}
    </div>
  );
}
